<template>
    <ExerciseForm v-if="editing"
                  v-model="form.fields"
                  :active-rest-timer="activeRestTimer"
                  :set-index="editingSetPosition.setIndex"
                  :set-group-index="editingSetPosition.setGroupIndex"
                  @cancel="handleExerciseCancel"
                  @delete="handleExerciseDelete"
                  @rest-timer-adjust="handleActiveRestTimerAdjust"
                  @rest-timer-skip="handleActiveRestTimerSkip"
                  @rest-timer-toggle="handleActiveRestTimerToggle"
                  @save="handleExerciseSave"
    />

    <div class="flex items-center justify-between mb-1">
        <Label class="!mb-0">Workout Items</Label>
        <div class="border rounded px-2 text-sm cursor-pointer" @click="$emit('toggle')" v-if="localItems.length > 1">
            {{ sorting ? 'Done Sorting' : 'Sort Exercises' }}
        </div>
    </div>

    <div class="mb-3">
        <draggable v-model="localItems"
                   @start="drag=true"
                   @end="drag=false"
                   item-key="key"
                   handle=".drag-handle"
                   chosen-class="chosen"
        >
            <template #item="{element, index}">
                <div v-if="element.type === 'exercise'"
                     :class="workoutItemClasses(index)"
                     :data-circuit-item="isCircuitItem(index) ? 'exercise' : null"
                     :data-workout-item-key="element.key"
                    :ref="component => setItemRef(element.key, component)">
                    <div v-if="startsCircuitRound(index)"
                         class="flex items-center justify-between border-b border-brand-primary-500/40 bg-brand-primary-500/10 px-3 py-2"
                         data-cy="circuit-round-header">
                        <span class="truncate font-semibold text-brand-primary-300">
                            {{ circuitLabel(element.exercise) }}
                        </span>
                        <span class="ml-3 shrink-0 rounded bg-brand-primary-950 px-2 py-1 text-xs font-semibold text-brand-primary-300">
                            Round {{ element.exercise.circuitRound }} / {{ element.exercise.circuitRounds }}
                        </span>
                    </div>
                    <template v-if="exerciseSetsEnabled">
                        <div class="flex items-center justify-between gap-3 border-b border-neutral-200 px-3 py-2 dark:border-neutral-700"
                             data-cy="exercise-set-group"
                             v-if="['single', 'first', 'only'].includes(setGroupPosition(index))">
                            <div class="flex min-w-0 items-center gap-2">
                                <div class="truncate font-semibold">{{ movementName(element.exercise) }}</div>
                                <MovementDemoVideoButton :movement="element.exercise.movement"/>
                            </div>
                            <button type="button"
                                    class="flex shrink-0 items-center justify-center gap-1 rounded border border-neutral-500 px-3 py-2 text-sm"
                                    data-cy="add-set"
                                    v-if="!sorting && element.exercise.movement"
                                    @click.stop="handleSetAdd(setGroupLastItemKey(element.exercise) || element.key)">
                                <Icon fixedWidth icon="plus"/>
                                Set
                            </button>
                        </div>
                        <div class="flex items-stretch bg-neutral-50 dark:bg-white/[2%]">
                            <div v-if="setCount(element.exercise) > 1"
                                 class="flex w-16 shrink-0 items-center justify-center border-r border-neutral-200 text-xs font-semibold text-neutral-600 dark:border-neutral-700 dark:text-neutral-400"
                                 data-cy="exercise-set-label">
                                Set {{ setNumber(element.exercise) }}
                            </div>
                            <Row class="min-w-0 flex-1 cursor-pointer"
                                 :exercise="element.exercise"
                                 :lastExercise="lastExerciseFor(element.exercise)"
                                 :last-exercise-loading="lastExerciseIsLoading(element.exercise)"
                                 @click="handleExerciseEdit(element.exercise)"
                                 :sortable="sorting"
                                 :bordered="false"
                                 :compact="true"
                                 :removable="true"
                                 :show-movement-name="false"
                                 @delete="handleExerciseRemove(element.key)"
                            />
                        </div>
                    </template>
                    <Row v-else
                         class="cursor-pointer"
                         :exercise="element.exercise"
                         :lastExercise="lastExerciseFor(element.exercise)"
                         :last-exercise-loading="lastExerciseIsLoading(element.exercise)"
                         @click="handleExerciseEdit(element.exercise)"
                         :sortable="sorting"
                         :bordered="true"
                         :compact="false"
                         :show-movement-name="true"
                    />
                </div>
                <RestTimerBlock
                    v-else
                    :compact="isCircuitItem(index)"
                    :compact-last="endsCircuitRound(index)"
                    :countdown-sound-enabled="restTimerCountdownSound"
                    :duration="Number(element.restTimer.duration || restTimerDuration || 90)"
                    :label="element.restTimer.label"
                    :remaining-value="element.restTimer.remaining ?? null"
                    :removable="element.restTimer.source === 'custom'"
                    :rank="element.restTimer.rank"
                    :source="element.restTimer.source || 'custom'"
                    :sortable="sorting"
                    :status="element.restTimer.status || 'ready'"
                    :template-id="templateId"
                    :template-item-rank="element.restTimer.templateItemRank"
                    :data-workout-item-key="element.key"
                    :data-circuit-item="isCircuitItem(index) ? 'rest' : null"
                    :ref="component => {
                        setRestTimerRef(element.key, component);
                        setItemRef(element.key, component);
                    }"
                    @change="handleRestTimerChange(element.key, $event)"
                    @remove="handleRestTimerRemove(element.key)"
                />
            </template>
        </draggable>
    </div>

    <div class="grid grid-cols-1 gap-3">
        <Button block primary outlined @click="handleExerciseAdd">
            Add Exercise
        </Button>
        <Button v-if="restTimerEnabled" block secondary outlined @click="handleRestTimerAdd">
            <Icon fixedWidth icon="stopwatch"/>
            Add Rest Timer
        </Button>
    </div>
</template>

<script>
import {defineComponent} from 'vue'
import {mapActions} from 'vuex';
import Row from '@/components/Exercise/Row.vue';
import RestTimerBlock from '@/components/RestTimerBlock.vue';
import ExerciseForm from '@/components/Exercise/Form.vue';
import MovementDemoVideoButton from '@/components/Movement/DemoVideoButton.vue';
import {Exercise, Form} from '@/internal.js';
import {uuid} from '@/helpers.js';
import draggable from 'vuedraggable'
import {capturePostHogEvent} from '@/utils/posthog';

const defaultExercise = {
  advanced: false,
  bandIds: [],
  duration: 0,
  exhaustionLevel: null,
  movementId: null,
  notes: null,
  partialRepetitions: null,
  rank: null,
  repetitions: null,
  setId: null,
  weight: null,
};

export default defineComponent({
  name: 'ExercisesInput',
  components: {
    draggable,
    ExerciseForm,
    MovementDemoVideoButton,
    RestTimerBlock,
    Row,
  },
  emits: [
    'update:modelValue',
    'update:restTimers',
    'toggle',
  ],
  data() {
    return {
      drag: false,
      editing: false,
      editingRestTimerKey: null,
      form: new Form({
        ...defaultExercise,
      }),
      lastExerciseIds: {},
      lastExerciseLoading: {},
      lastExerciseRequestId: 0,
      lastExerciseRequestIds: {},
      localItems: [],
      itemRefs: {},
      pendingExerciseInsertAfterSet: null,
      pendingExerciseInsertAfterKey: null,
      restTimerRefs: {},
      syncing: false,
    };
  },
  computed: {
    activeRestTimer() {
      if (this.editingRestTimerKey) {
        const item = this.localItems.find(item => item.type === 'rest_timer' && item.key === this.editingRestTimerKey);

        if (item && ['running', 'paused'].includes(item.restTimer?.status || 'ready')) {
          return item.restTimer;
        }

        return null;
      }

      const exerciseIndex = this.editingExerciseIndex;

      if (exerciseIndex <= 0) {
        return null;
      }

      const item = this.localItems[exerciseIndex - 1];

      if (item?.type !== 'rest_timer' || !['running', 'paused'].includes(item.restTimer?.status || 'ready')) {
        return null;
      }

      return item?.restTimer || null;
    },
    editingExerciseIndex() {
      if (!this.editing || !this.form.fields) {
        return -1;
      }

      const editingId = this.form.fields.id;
      const idIndex = editingId ? this.localItems.findIndex(item => {
        return item.type === 'exercise' && item.exercise.id && String(editingId) === String(item.exercise.id);
      }) : -1;

      if (idIndex >= 0) {
        return idIndex;
      }

      // Autosaves recreate exercise records on the API. If their background
      // reload completes while this form is open, the database ID changes but
      // the movement and rank still identify the row the user is editing.
      const editingRank = Number(this.form.fields.rank);
      const editingMovementId = this.form.fields.movementId || this.form.fields.movement?.id || null;

      if (editingRank) {
        const rankIndex = this.localItems.findIndex(item => {
          const itemMovementId = item.exercise?.movementId || item.exercise?.movement?.id || null;

          return item.type === 'exercise'
            && Number(item.exercise.rank) === editingRank
            && (!editingMovementId || String(itemMovementId) === String(editingMovementId));
        });

        if (rankIndex >= 0) {
          return rankIndex;
        }
      }

      return this.localItems.findIndex(item => {
        return item.type === 'exercise' && this.exerciseKey(this.form.fields) === this.exerciseKey(item.exercise);
      });
    },
    exerciseItems() {
      return this.localItems
        .filter(item => item.type === 'exercise')
        .map(item => item.exercise);
    },
    editingSetPosition() {
      return this.exerciseSetPosition(this.form.fields);
    },
    lastExercisesKey() {
      return this.exerciseItems
        .map((exercise, index) => {
          const setPosition = this.exerciseSetPosition(exercise);

          return [
            this.exerciseKey(exercise, index),
            exercise.id || '',
            exercise.movementId || exercise.movement?.id || '',
            this.previousRepsSourceIsSelected ? this.previousExerciseBandIds(exercise).join(',') : 'any',
            exercise.rank || '',
            exercise.workoutId || '',
            setPosition.setIndex || '',
            setPosition.setGroupIndex || '',
          ].join(':');
        })
        .join('|');
    },
    nextRank() {
      return this.localItems.length + 1;
    },
    previousRepsSourceIsSelected() {
      const source = this.user?.settings?.metaPrevRepsSource ?? this.user?.settings?.meta_prev_reps_source ?? 'selected';

      return source === 'selected';
    },
    user() {
      return this.$store.getters['auth/user'];
    },
  },
  methods: {
    ...mapActions({
      exerciseLast: 'exercise/last',
    }),
    rankLocalItems(items) {
      return items.map((item, index) => {
        const rank = index + 1;

        if (item.type === 'exercise') {
          return {
            ...item,
            rank,
            exercise: new Exercise({
              ...item.exercise,
              rank,
            }),
          };
        }

        return {
          ...item,
          rank,
          restTimer: {
            ...item.restTimer,
            rank,
          },
        };
      });
    },
    buildLocalItems(exercises, restTimers) {
      const exerciseItems = (exercises || []).map((exercise, index) => {
        const rank = Number(exercise.rank || index + 1);
        const exerciseModel = exercise instanceof Exercise ? exercise : new Exercise(exercise);
        exerciseModel.rank = rank;

        return {
          key: `exercise-${this.exerciseKey(exercise, index)}`,
          type: 'exercise',
          rank,
          exercise: exerciseModel,
        };
      });

      const restTimerItems = (restTimers || []).map((timer, index) => {
        const key = timer.key || uuid();
        const rank = Number(timer.rank || timer.templateItemRank || timer.template_item_rank || index + 1);

        return {
          key,
          type: 'rest_timer',
          rank,
          restTimer: {
            key,
            circuitId: timer.circuitId || timer.circuit_id || null,
            circuitLabel: timer.circuitLabel || timer.circuit_label || null,
            circuitRound: timer.circuitRound || timer.circuit_round || null,
            circuitRounds: timer.circuitRounds || timer.circuit_rounds || null,
            circuitStepId: timer.circuitStepId || timer.circuit_step_id || null,
            afterCircuitId: timer.afterCircuitId || timer.after_circuit_id || null,
            label: timer.label || 'Rest Timer',
            duration: Number(timer.duration || 90),
            rank,
            remaining: Number(timer.remaining ?? timer.duration ?? 90),
            source: timer.source || 'custom',
            status: timer.status || 'ready',
            templateItemRank: timer.templateItemRank || timer.template_item_rank || null,
          },
        };
      });

      return [
        ...exerciseItems,
        ...restTimerItems,
      ].sort((a, b) => a.rank > b.rank ? 1 : -1);
    },
    emitItems() {
      const exercises = [];
      const restTimers = [];

      this.localItems.forEach((item, index) => {
        const rank = index + 1;

        if (item.type === 'exercise') {
          exercises.push(new Exercise({
            ...item.exercise,
            rank,
          }));
          return;
        }

        restTimers.push({
          ...item.restTimer,
          key: item.key,
          rank,
        });
      });

      if (JSON.stringify(exercises) !== JSON.stringify(this.modelValue)) {
        this.$emit('update:modelValue', exercises);
      }

      if (JSON.stringify(restTimers) !== JSON.stringify(this.restTimers)) {
        this.$emit('update:restTimers', restTimers);
      }
    },
    exerciseKey(exercise, index = null) {
      return exercise.id || [
        exercise.movementId || exercise.movement?.id || 'movement',
        exercise.rank || 'rank',
        index ?? this.exerciseItems.indexOf(exercise),
      ].join('-');
    },
    exerciseMatchesEditingForm(exercise) {
      const editingExercise = this.form.fields || {};

      if (exercise?.id && editingExercise.id) {
        return String(exercise.id) === String(editingExercise.id);
      }

      return this.exerciseKey(exercise) === this.exerciseKey(editingExercise);
    },
    currentEditingExerciseIndex() {
      const exerciseIndex = this.editingExerciseIndex;

      if (exerciseIndex >= 0) {
        return exerciseIndex;
      }

      const restTimerIndex = this.editingRestTimerKey
        ? this.localItems.findIndex(item => item.type === 'rest_timer' && item.key === this.editingRestTimerKey)
        : -1;
      const nextItem = this.localItems[restTimerIndex + 1];

      return restTimerIndex >= 0 && nextItem?.type === 'exercise' ? restTimerIndex + 1 : -1;
    },
    handleExerciseAdd() {
      this.editingRestTimerKey = null;
      this.pendingExerciseInsertAfterSet = null;
      this.pendingExerciseInsertAfterKey = null;
      this.form.fields = {
        ...defaultExercise,
      };
      this.editing = true;
    },
    handleExerciseCancel() {
      const activeRestTimerKey = this.activeRestTimer?.key || this.editingRestTimerKey || null;

      this.editingRestTimerKey = null;
      this.pendingExerciseInsertAfterSet = null;
      this.pendingExerciseInsertAfterKey = null;
      this.form.fields = {};
      this.editing = false;

      this.deferScrollItemIntoView(activeRestTimerKey, {
        block: 'center',
      });
    },
    handleExerciseDelete() {
      this.editingRestTimerKey = null;
      this.pendingExerciseInsertAfterSet = null;
      this.pendingExerciseInsertAfterKey = null;
      this.localItems = this.rankLocalItems(this.localItems.filter((item) => {
        if (item.type !== 'exercise') {
          return true;
        }

        if (this.exerciseMatchesEditingForm(item.exercise)) {
          return false;
        }

        return true;
      }));
      this.editing = false;
    },
    handleExerciseRemove(key) {
      const item = this.localItems.find((item) => item.key === key && item.type === 'exercise');

      this.pendingExerciseInsertAfterSet = null;
      this.pendingExerciseInsertAfterKey = null;
      this.localItems = this.rankLocalItems(this.localItems.filter((item) => item.key !== key));

      if (item && this.setGroupKey(item.exercise)) {
        capturePostHogEvent('workout_exercise_set_removed', {
          movement_id: item.exercise.movementId || item.exercise.movement?.id || null,
          set_count_before_remove: this.setCount(item.exercise),
        });
      }
    },
    handleExerciseEdit(exercise, options = {}) {

      if (this.sorting) {
        return;
      }

      this.editingRestTimerKey = options.restTimerKey || null;
      this.form.fields = {
        ...defaultExercise,
        ...exercise,
      };
      this.editing = true;
    },
    handleExerciseSave() {
      const editingItemIndex = this.currentEditingExerciseIndex();
      let savedExerciseKey = editingItemIndex >= 0 ? this.localItems[editingItemIndex]?.key || null : null;

      if (this.form.fields.id) {
        this.localItems = this.rankLocalItems([
          ...this.localItems.map((item, index) => {
            if (item.type === 'exercise' && (index === editingItemIndex || item.key === savedExerciseKey || this.exerciseMatchesEditingForm(item.exercise))) {
              savedExerciseKey = item.key;

              return {
                ...item,
                exercise: new Exercise({
                  ...this.form.fields,
                  rank: item.rank,
                }),
              };
            }

            return item;
          }),
        ]);
      } else {
        const id = uuid();
        savedExerciseKey = `exercise-${id}`;
        const newExerciseItem = {
          key: `exercise-${id}`,
          type: 'exercise',
          rank: this.nextRank,
          exercise: new Exercise({
            ...this.form.fields,
            id,
            rank: this.nextRank,
          }),
        };

        if (this.pendingExerciseInsertAfterKey) {
          const insertAfterIndex = this.pendingExerciseInsertAfterIndex();

          if (insertAfterIndex >= 0) {
            this.localItems = this.rankLocalItems([
              ...this.localItems.slice(0, insertAfterIndex + 1),
              newExerciseItem,
              ...this.localItems.slice(insertAfterIndex + 1),
            ]);
          } else {
            this.localItems = this.rankLocalItems([
              ...this.localItems,
              newExerciseItem,
            ]);
          }
        } else {
          this.localItems = this.rankLocalItems([
            ...this.localItems,
            newExerciseItem,
          ]);
        }
      }

      const followingRestTimer = this.handleFollowingRestTimerAutoAdvance(savedExerciseKey, editingItemIndex);
      const followingRestTimerKey = followingRestTimer?.key || null;
      const scrollTargetKey = followingRestTimerKey || savedExerciseKey;
      this.pendingExerciseInsertAfterSet = null;
      this.pendingExerciseInsertAfterKey = null;
      this.editing = false;
      this.form.fields = {};

      this.$nextTick(() => {
        this.scrollItemIntoView(scrollTargetKey, {
          block: followingRestTimerKey ? 'center' : 'nearest',
        });

        if (followingRestTimerKey && this.restTimerAutoOpenNextExerciseOnComplete) {
          this.handleNextExerciseOpen(followingRestTimerKey, {
            trigger: followingRestTimer.started ? 'rest_timer_auto_started' : 'rest_timer_already_active',
          });
        }
      });
    },
    handleSetAdd(key) {
      if (!this.exerciseSetsEnabled) {
        return;
      }

      const item = this.localItems.find(item => item.key === key && item.type === 'exercise');

      if (!item) {
        return;
      }

      const setId = item.exercise.setId || item.exercise.set_id || uuid();
      const setCountBeforeAdd = this.setCount(item.exercise);

      const itemsWithSetId = this.localItems.map((localItem) => {
        if (localItem.key !== key || localItem.type !== 'exercise') {
          return localItem;
        }

        return {
          ...localItem,
          exercise: new Exercise({
            ...localItem.exercise,
            setId,
          }),
        };
      });

      const insertAfterIndex = itemsWithSetId
        .map((localItem, index) => ({localItem, index}))
        .filter(({localItem}) => {
          return localItem.type === 'exercise' && this.setGroupKey(localItem.exercise) === `set:${setId}`;
        })
        .map(({index}) => index)
        .pop();
      const id = uuid();
      const newExerciseKey = `exercise-${id}`;
      const newExerciseItem = {
        key: newExerciseKey,
        type: 'exercise',
        rank: Number(insertAfterIndex) + 2,
        exercise: new Exercise({
          ...item.exercise,
          id,
          rank: Number(insertAfterIndex) + 2,
          setId,
        }),
      };

      this.localItems = this.rankLocalItems([
        ...itemsWithSetId.slice(0, insertAfterIndex + 1),
        newExerciseItem,
        ...itemsWithSetId.slice(insertAfterIndex + 1),
      ]);
      this.pendingExerciseInsertAfterKey = null;
      this.pendingExerciseInsertAfterSet = null;

      this.$nextTick(() => {
        this.scrollItemIntoView(newExerciseKey, {
          block: 'nearest',
        });
      });

      capturePostHogEvent('workout_exercise_set_added', {
        movement_id: item.exercise.movementId || item.exercise.movement?.id || null,
        set_count_before_add: setCountBeforeAdd,
      });
    },
    pendingExerciseInsertAfterIndex() {
      const keyIndex = this.localItems.findIndex(item => item.key === this.pendingExerciseInsertAfterKey);

      if (keyIndex >= 0) {
        return keyIndex;
      }

      if (!this.pendingExerciseInsertAfterSet?.setId) {
        return -1;
      }

      const groupKey = `set:${this.pendingExerciseInsertAfterSet.setId}`;

      return this.localItems
        .map((item, index) => ({item, index}))
        .filter(({item}) => {
          if (item.type !== 'exercise' || this.setGroupKey(item.exercise) !== groupKey) {
            return false;
          }

          const movementId = item.exercise.movementId || item.exercise.movement?.id || null;

          return !this.pendingExerciseInsertAfterSet.movementId || movementId === this.pendingExerciseInsertAfterSet.movementId;
        })
        .map(({index}) => index)
        .pop() ?? -1;
    },
    handleRestTimerAdd() {
      const duration = Number(this.restTimerDuration || 90);
      const key = uuid();

      this.localItems = this.rankLocalItems([
        ...this.localItems,
        {
          key,
          type: 'rest_timer',
          rank: this.nextRank,
          restTimer: {
            key,
            source: 'custom',
            duration,
            label: 'Rest Timer',
            rank: this.nextRank,
            remaining: duration,
            status: 'ready',
          },
        },
      ]);

      capturePostHogEvent('workout_rest_timer_added', {
        duration,
        used_template: !!this.templateId,
      });
    },
    handleRestTimerChange(key, changes) {
      let shouldAutoOpenNextExercise = false;
      const starting = changes?.status === 'running';
      const startingItem = this.localItems.find(item => item.type === 'rest_timer' && item.key === key);

      this.localItems = this.localItems.map((item) => {
        if (item.type !== 'rest_timer') {
          return item;
        }

        if (item.key !== key) {
          if (!starting || item.restTimer?.status !== 'running') {
            return item;
          }

          return {
            ...item,
            restTimer: {
              ...item.restTimer,
              remaining: item.rank > startingItem?.rank ? Number(item.restTimer.duration || this.restTimerDuration || 90) : 0,
              status: item.rank > startingItem?.rank ? 'ready' : 'skipped',
            },
          };
        }

        shouldAutoOpenNextExercise = this.shouldAutoOpenNextExercise(item.restTimer, changes);

        return {
          ...item,
          restTimer: {
            ...item.restTimer,
            ...changes,
          },
        };
      });

      if (shouldAutoOpenNextExercise) {
        this.$nextTick(() => {
          this.handleNextExerciseOpen(key, {
            trigger: 'rest_timer_started',
          });
        });
      }
    },
    handleRestTimerRemove(key) {
      this.localItems = this.rankLocalItems(this.localItems.filter(item => item.key !== key));
      this.setItemRef(key, null);
      this.setRestTimerRef(key, null);

      capturePostHogEvent('workout_rest_timer_removed', {
        used_template: !!this.templateId,
      });
    },
    handleActiveRestTimerAdjust(seconds) {
      const restTimer = this.activeRestTimer;

      if (!restTimer) {
        return;
      }

      const component = this.restTimerRefs[restTimer.key];

      if (component?.handleAdjust) {
        component.handleAdjust(seconds);
        return;
      }

      this.handleRestTimerChange(restTimer.key, {
        remaining: Math.max(0, Number(restTimer.remaining || 0) + seconds),
      });
    },
    handleActiveRestTimerSkip() {
      const restTimer = this.activeRestTimer;

      if (!restTimer) {
        return;
      }

      const component = this.restTimerRefs[restTimer.key];

      if (component?.handleSkip) {
        component.handleSkip();
        return;
      }

      this.handleRestTimerChange(restTimer.key, {
        remaining: 0,
        status: 'skipped',
      });
    },
    handleActiveRestTimerToggle() {
      const restTimer = this.activeRestTimer;

      if (!restTimer) {
        return;
      }

      const component = this.restTimerRefs[restTimer.key];

      if (component?.handleToggle) {
        component.handleToggle();
      }
    },
    handleFollowingRestTimerAutoAdvance(exerciseKey, exerciseIndex = null) {
      if (!this.restTimerAutoStartOnExerciseSave || (!exerciseKey && exerciseIndex === null)) {
        return null;
      }

      const resolvedExerciseIndex = exerciseIndex ?? this.localItems.findIndex(item => item.key === exerciseKey);
      const restTimerIndex = resolvedExerciseIndex + 1;
      const nextItem = this.localItems[restTimerIndex];

      if (resolvedExerciseIndex < 0 || !nextItem || nextItem.type !== 'rest_timer') {
        return null;
      }

      const status = nextItem.restTimer?.status || 'ready';

      if (['running', 'paused'].includes(status)) {
        return {
          key: nextItem.key,
          started: false,
          status,
        };
      }

      if (!this.restTimerCanAutoStart(nextItem.restTimer)) {
        return null;
      }

      const duration = Number(nextItem.restTimer.duration || this.restTimerDuration || 90);

      this.localItems = this.localItems.map((item, index) => {
        if (item.type === 'rest_timer' && item.key !== nextItem.key && item.restTimer?.status === 'running') {
          return {
            ...item,
            restTimer: {
              ...item.restTimer,
              remaining: item.rank > nextItem.rank ? Number(item.restTimer.duration || this.restTimerDuration || 90) : 0,
              status: item.rank > nextItem.rank ? 'ready' : 'skipped',
            },
          };
        }

        if (index !== restTimerIndex) {
          return item;
        }

        return {
          ...item,
          restTimer: {
            ...item.restTimer,
            duration,
            remaining: duration,
            status: 'running',
          },
        };
      });

      capturePostHogEvent('workout_rest_timer_auto_started', {
        duration,
        used_template: !!this.templateId,
      });

      return {
        key: nextItem.key,
        started: true,
        status: 'running',
      };
    },
    handleNextExerciseOpen(restTimerKey, properties = {}) {
      const restTimerIndex = this.localItems.findIndex(item => item.key === restTimerKey);
      const nextItem = this.localItems[restTimerIndex + 1];

      if (restTimerIndex < 0 || !nextItem || nextItem.type !== 'exercise') {
        return;
      }

      this.handleExerciseEdit(nextItem.exercise, {
        restTimerKey,
      });

      capturePostHogEvent('workout_rest_timer_next_exercise_opened', {
        ...properties,
        used_template: !!this.templateId,
      });
    },
    restTimerCanAutoStart(restTimer) {
      return ['ready', null, undefined].includes(restTimer?.status);
    },
    deferScrollItemIntoView(key, options = {}) {
      this.$nextTick(() => {
        [100, 350, 700].forEach((delay) => {
          window.setTimeout(() => {
            this.scrollItemIntoView(key, options);
          }, delay);
        });
      });
    },
    scrollItemIntoView(key, options = {}) {
      if (!key) {
        return;
      }

      const component = this.itemRefs[key];
      const escapedKey = window.CSS?.escape ? window.CSS.escape(key) : String(key).replace(/"/g, '\\"');
      const element = component?.$el || component || document.querySelector(`[data-workout-item-key="${escapedKey}"]`);

      if (!element?.getBoundingClientRect) {
        return;
      }

      const rect = element.getBoundingClientRect();
      const targetTop = window.scrollY + rect.top - ((window.innerHeight - rect.height) / 2);

      window.scrollTo({
        behavior: options.behavior || 'auto',
        top: Math.max(0, targetTop),
      });
    },
    setItemRef(key, component) {
      if (!key) {
        return;
      }

      if (component) {
        this.itemRefs[key] = component;
        return;
      }

      delete this.itemRefs[key];
    },
    setRestTimerRef(key, component) {
      if (!key) {
        return;
      }

      if (component) {
        this.restTimerRefs[key] = component;
        return;
      }

      delete this.restTimerRefs[key];
    },
    exerciseItemClasses(index) {
      if (this.isCircuitItem(index)) {
        const start = this.startsCircuitRound(index) ? 'mt-4 rounded-t border-t' : '';
        const rounded = this.endsCircuitRound(index) ? 'rounded-b' : '';

        return `overflow-hidden border-x border-b border-neutral-200 bg-white dark:border-neutral-700 dark:bg-white/[2%] ${start} ${rounded}`;
      }

      const position = this.setGroupPosition(index);

      const base = 'overflow-hidden border-neutral-200 bg-white dark:border-neutral-700 dark:bg-white/[2%]';

      if (['single', 'only'].includes(position)) {
        return `${base} my-2 rounded border`;
      }

      if (position === 'first') {
        return `${base} mt-2 rounded-t border-x border-t`;
      }

      if (position === 'last') {
        return `${base} mb-2 rounded-b border-x border-t border-b`;
      }

      return `${base} border-x border-t`;
    },
    workoutItemClasses(index) {
      if (!this.exerciseSetsEnabled && !this.isCircuitItem(index)) {
        return null;
      }

      return this.exerciseItemClasses(index);
    },
    movementName(exercise) {
      return exercise.movement ? exercise.movement.name : 'Movement';
    },
    circuitLabel(exercise) {
      return exercise.circuitLabel || 'Circuit';
    },
    startsCircuitRound(index) {
      const item = this.localItems[index];

      if (item?.type !== 'exercise' || !item.exercise.circuitId || !item.exercise.circuitRound) {
        return false;
      }

      return this.localItems.findIndex(candidate => {
        return candidate.type === 'exercise'
          && candidate.exercise.circuitId === item.exercise.circuitId
          && candidate.exercise.circuitRound === item.exercise.circuitRound;
      }) === index;
    },
    circuitContext(index) {
      const item = this.localItems[index];

      if (!item) {
        return null;
      }

      if (item.type === 'exercise') {
        const exercise = item.exercise;

        if (!exercise?.circuitId || !exercise?.circuitRound) {
          return null;
        }

        return {
          id: exercise.circuitId,
          round: Number(exercise.circuitRound),
        };
      }

      const timer = item.restTimer || {};
      const timerCircuitId = timer.circuitId || timer.circuit_id || null;
      const timerCircuitRound = timer.circuitRound || timer.circuit_round || null;

      if (timerCircuitId && timerCircuitRound) {
        return {
          id: timerCircuitId,
          round: Number(timerCircuitRound),
        };
      }

      const previous = this.localItems[index - 1];
      const next = this.localItems[index + 1];

      if (previous?.type !== 'exercise' || next?.type !== 'exercise') {
        return null;
      }

      const previousCircuitId = previous.exercise?.circuitId;
      const nextCircuitId = next.exercise?.circuitId;

      if (!previousCircuitId || previousCircuitId !== nextCircuitId) {
        return null;
      }

      return {
        id: previousCircuitId,
        round: Number(previous.exercise.circuitRound),
      };
    },
    isCircuitItem(index) {
      return !!this.circuitContext(index);
    },
    endsCircuitRound(index) {
      const current = this.circuitContext(index);

      if (!current) {
        return false;
      }

      const next = this.circuitContext(index + 1);

      return !next || next.id !== current.id || next.round !== current.round;
    },
    setGroupPosition(index) {
      const item = this.localItems[index];

      if (!item || item.type !== 'exercise') {
        return 'single';
      }

      const key = this.setGroupKey(item.exercise);

      if (!key || this.setCount(item.exercise) < 2) {
        return 'single';
      }

      const previousItem = this.localItems[index - 1];
      const nextItem = this.localItems[index + 1];
      const hasPreviousSet = previousItem?.type === 'exercise' && this.setGroupKey(previousItem.exercise) === key;
      const hasNextSet = nextItem?.type === 'exercise' && this.setGroupKey(nextItem.exercise) === key;

      if (!hasPreviousSet && !hasNextSet) {
        return 'only';
      }

      if (!hasPreviousSet) {
        return 'first';
      }

      if (!hasNextSet) {
        return 'last';
      }

      return 'middle';
    },
    setCount(exercise) {
      const key = this.setGroupKey(exercise);

      if (!key) {
        return 1;
      }

      return this.exerciseItems.filter(item => this.setGroupKey(item) === key).length;
    },
    setIndex(exercise) {
      const key = this.setGroupKey(exercise);

      if (!key) {
        return null;
      }

      const exercises = this.exerciseItems.filter(item => this.setGroupKey(item) === key);
      const existingIndex = exercises.findIndex(item => this.sameExercise(item, exercise));

      if (existingIndex >= 0) {
        return existingIndex + 1;
      }

      if (this.pendingExerciseInsertAfterSet?.setId && key === `set:${this.pendingExerciseInsertAfterSet.setId}`) {
        return exercises.length + 1;
      }

      return null;
    },
    setNumber(exercise) {
      return this.setIndex(exercise) || 1;
    },
    setGroupIndex(exercise) {
      const key = this.setGroupKey(exercise);
      const movementId = this.movementIdFor(exercise);

      if (!key || !movementId) {
        return null;
      }

      const groupKeys = [];

      this.exerciseItems.forEach((item) => {
        if (this.movementIdFor(item) !== movementId) {
          return;
        }

        const itemGroupKey = this.setGroupKey(item) || `single:${this.exerciseKey(item)}`;

        if (!groupKeys.includes(itemGroupKey)) {
          groupKeys.push(itemGroupKey);
        }
      });

      const index = groupKeys.indexOf(key);

      return index >= 0 ? index + 1 : null;
    },
    exerciseSetPosition(exercise) {
      return {
        setIndex: this.setIndex(exercise),
        setGroupIndex: this.setGroupIndex(exercise),
      };
    },
    movementIdFor(exercise) {
      return exercise?.movementId || exercise?.movement?.id || null;
    },
    sameExercise(left, right) {
      if (left === right) {
        return true;
      }

      return !!left?.id && !!right?.id && left.id === right.id;
    },
    setGroupLastItemKey(exercise) {
      const key = this.setGroupKey(exercise);

      if (!key) {
        return null;
      }

      const item = [...this.localItems].reverse().find(item => {
        return item.type === 'exercise' && this.setGroupKey(item.exercise) === key;
      });

      return item?.key || null;
    },
    setGroupKey(exercise) {
      const setId = exercise.setId || exercise.set_id;

      return setId ? `set:${setId}` : null;
    },
    shouldAutoOpenNextExercise(restTimer, changes) {
      if (!this.restTimerAutoOpenNextExerciseOnComplete || this.sorting || this.editing) {
        return false;
      }

      return restTimer?.status !== 'running' && changes?.status === 'running';
    },
    persistedExerciseId(exercise) {
      const id = Number(exercise.id);

      return Number.isInteger(id) && id > 0 ? id : null;
    },
    previousExerciseBandIds(exercise) {
      if (!exercise) {
        return [];
      }

      if (exercise.bandIds && exercise.bandIds.length) {
        return exercise.bandIds;
      }

      if (exercise.band_ids && exercise.band_ids.length) {
        return exercise.band_ids;
      }

      if (exercise.bands && exercise.bands.length) {
        return exercise.bands.map(band => band.id).filter(Boolean);
      }

      return [];
    },
    lastExerciseFor(exercise) {
      const lastExerciseId = this.lastExerciseIds[this.exerciseKey(exercise)];

      return lastExerciseId ? this.$store.getters['exercise/findBy'](lastExerciseId, 'id') : null;
    },
    lastExerciseIsLoading(exercise) {
      return Boolean(this.lastExerciseLoading[this.exerciseKey(exercise)]);
    },
    loadLastExercises() {
      this.exerciseItems.forEach((exercise, index) => {
        const movementId = exercise.movementId || exercise.movement?.id;

        if (!movementId) {
          return;
        }

        const key = this.exerciseKey(exercise, index);
        const setPosition = this.exerciseSetPosition(exercise);
        const requestId = ++this.lastExerciseRequestId;

        this.lastExerciseLoading = {
          ...this.lastExerciseLoading,
          [key]: true,
        };
        this.lastExerciseRequestIds = {
          ...this.lastExerciseRequestIds,
          [key]: requestId,
        };

        this.exerciseLast({
          params: {
            exerciseId: this.persistedExerciseId(exercise),
            movementId,
            bandIds: this.previousRepsSourceIsSelected ? this.previousExerciseBandIds(exercise) : [],
            rank: exercise.rank,
            setGroupIndex: setPosition.setGroupIndex,
            setIndex: setPosition.setIndex,
            workoutId: exercise.workoutId,
          },
        }).then((ids) => {
          if (this.lastExerciseRequestIds[key] !== requestId) {
            return;
          }

          this.lastExerciseIds = {
            ...this.lastExerciseIds,
            [key]: ids.length ? ids[0] : null,
          };
        }).catch(() => {
          if (this.lastExerciseRequestIds[key] !== requestId) {
            return;
          }

          this.lastExerciseIds = {
            ...this.lastExerciseIds,
            [key]: null,
          };
        }).finally(() => {
          if (this.lastExerciseRequestIds[key] !== requestId) {
            return;
          }

          this.lastExerciseLoading = {
            ...this.lastExerciseLoading,
            [key]: false,
          };
        });
      });
    },
    syncLocalItems() {
      const nextItems = this.preserveRestTimerRuntimeState(
        this.buildLocalItems(this.modelValue, this.restTimers),
      );

      if (JSON.stringify(nextItems) === JSON.stringify(this.localItems)) {
        return;
      }

      this.syncing = true;
      this.localItems = nextItems;
      this.$nextTick(() => {
        this.syncing = false;
      });
    },
    preserveRestTimerRuntimeState(nextItems) {
      return nextItems.map((item) => {
        if (item.type !== 'rest_timer') {
          return item;
        }

        const currentItem = this.localItems.find((localItem) => {
          return localItem.type === 'rest_timer' && localItem.key === item.key;
        });

        if (!currentItem) {
          return item;
        }

        return {
          ...item,
          restTimer: {
            ...item.restTimer,
            remaining: currentItem.restTimer.remaining,
            status: currentItem.restTimer.status || 'ready',
          },
        };
      });
    },
  },
  props: {
    modelValue: {
      required: true,
      type: Array,
    },
    restTimerDuration: {
      default: 90,
      required: false,
      type: Number,
    },
    restTimerCountdownSound: {
      default: true,
      required: false,
      type: Boolean,
    },
    restTimerAutoStartOnExerciseSave: {
      default: false,
      required: false,
      type: Boolean,
    },
    restTimerAutoOpenNextExerciseOnComplete: {
      default: false,
      required: false,
      type: Boolean,
    },
    restTimerEnabled: {
      default: false,
      required: false,
      type: Boolean,
    },
    exerciseSetsEnabled: {
      default: true,
      required: false,
      type: Boolean,
    },
    restTimers: {
      default: () => [],
      required: false,
      type: Array,
    },
    sorting: {
      required: false,
      type: Boolean,
    },
    templateId: {
      default: null,
      required: false,
    }
  },
  watch: {
    localItems: {
      deep: true,
      handler() {
        if (!this.syncing) {
          this.emitItems();
        }
      },
    },
    modelValue: {
      deep: true,
      immediate: true,
      handler() {
        this.syncLocalItems();
      },
    },
    restTimers: {
      deep: true,
      immediate: true,
      handler() {
        this.syncLocalItems();
      },
    },
    lastExercisesKey: {
      immediate: true,
      handler() {
        this.loadLastExercises();
      },
    },
  },
});
</script>

<style scoped lang="scss">
.chosen {
    background: #888;
}
</style>
